<template>
  <div class="space-y-8">
    <!-- Transaction details -->
    <div>
      <div class="flex items-center gap-2 mb-3">
        <CurrencyEuroIcon class="h-5 w-5 text-indigo-500" />
        <h4 class="text-base font-semibold tracking-tight text-gray-900">{{ $gettext("Détails du compromis") }}</h4>
      </div>
      <div class="border border-gray-200 rounded-lg p-4">
        <div class="grid grid-cols-2 gap-3">
          <ExtractionInput v-model="data.signatureDate" type="date" :label="$gettext('Date de signature')" />
          <ExtractionInput v-model="data.finalPrice" type="number" :label="$gettext('Prix final')" />
          <ExtractionInput v-model="data.depositAmount" type="number" :label="$gettext('Montant de l\'acompte')" :defaulted="isDefaulted('depositAmount')" />
          <div>
            <label class="block text-xs font-medium text-gray-500 mb-1">{{ $gettext("Solde") }}</label>
            <div class="rounded-md bg-gray-50 px-3 py-2 text-sm text-gray-700 ring-1 ring-inset ring-gray-200">
              {{ computedBalance != null ? `${computedBalance.toLocaleString('fr-BE')} €` : '—' }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Bank account -->
    <div>
      <div class="flex items-center gap-2 mb-3">
        <BanknotesIcon class="h-5 w-5 text-indigo-500" />
        <h4 class="text-base font-semibold tracking-tight text-gray-900">{{ $gettext("Compte bancaire de l'acheteur") }}</h4>
      </div>
      <div class="border border-gray-200 rounded-lg p-4">
        <div class="grid grid-cols-2 gap-3">
          <ExtractionInput v-model="data.buyerAccountNumber" :label="$gettext('Numéro IBAN')" />
          <div>
            <label class="block text-xs font-medium text-gray-500 mb-1">{{ $gettext("Pays du compte") }}</label>
            <div class="flex items-center gap-2">
              <span
                v-if="data.buyerAccountCountryName"
                class="inline-flex items-center rounded-full bg-green-50 px-2.5 py-1 text-xs font-medium text-green-700 ring-1 ring-inset ring-green-600/20"
              >
                {{ data.buyerAccountCountryName }}
              </span>
              <span
                v-else
                class="inline-flex items-center rounded-full bg-yellow-50 px-2.5 py-1 text-xs font-medium text-yellow-700 ring-1 ring-inset ring-yellow-600/20"
              >
                {{ $gettext("Non détecté") }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <p v-if="!isValid" class="text-xs text-red-600">
      {{ $gettext("Veuillez vérifier que le prix final et la date de signature sont renseignés.") }}
    </p>
  </div>
</template>

<script setup>
import { reactive, computed } from "vue";
import { CurrencyEuroIcon, BanknotesIcon } from "@heroicons/vue/24/outline";
import ExtractionInput from "@/components/ui/ExtractionInput.vue";

const props = defineProps({
  extractionData: { type: Object, required: true },
  // Fields the document did not contain, filled from the organization's settings.
  defaultedFields: { type: Array, default: () => [] }
});

const isDefaulted = (field) => props.defaultedFields.includes(field);

const data = reactive({
  signatureDate: props.extractionData.signatureDate || "",
  finalPrice: props.extractionData.finalPrice || null,
  depositAmount: props.extractionData.depositAmount || null,
  buyerAccountNumber: props.extractionData.buyerAccountNumber || "",
  buyerAccountCountryId: props.extractionData.buyerAccountCountryId || null,
  buyerAccountCountryName: props.extractionData.buyerAccountCountryName || null
});

const computedBalance = computed(() => {
  if (data.finalPrice == null) return null;
  return (data.finalPrice || 0) - (data.depositAmount || 0);
});

const isValid = computed(() => {
  return data.finalPrice != null && data.signatureDate;
});

const getReviewedData = () => ({
  signatureDate: data.signatureDate,
  finalPrice: data.finalPrice,
  depositAmount: data.depositAmount,
  buyerAccountNumber: data.buyerAccountNumber,
  buyerAccountCountryId: data.buyerAccountCountryId
});

defineExpose({ getReviewedData, isValid });
</script>
